'use client';

import { useApolloClient } from '@apollo/client';
import { isNil } from 'es-toolkit';
import { toast } from 'sonner';
import { TaxEnrollmentsQuery } from 'src/components/advisor-dashboard/taxes/tax-content';
import { BulkUpdateTaxEnrollmentsToNotInterestedMutation } from 'src/components/advisor-dashboard/taxes/tax-enrollment-mutations';
import { HouseholdTaxesQuery } from 'src/pages/advisor/dashboard/client/[salesforceAccountId]/tax';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { SelectedClient } from '../../tax-utils';

export function useBulkMarkNotInterested() {
  const client = useApolloClient();
  const [bulkUpdateTaxEnrollmentsToNotInterested] = useMutationWithErrorLogging(
    BulkUpdateTaxEnrollmentsToNotInterestedMutation,
  );

  async function invalidateAndRefetchCaches() {
    const cache = client.cache;
    cache.evict({ fieldName: 'taxEnrollmentsTableRows' });
    cache.gc();
    await Promise.all([
      client.refetchQueries({ include: [TaxEnrollmentsQuery] }),
      client.refetchQueries({ include: [HouseholdTaxesQuery] }),
    ]);
  }

  async function bulkMarkNotInterested(eligibleClients: readonly SelectedClient[]) {
    try {
      const clientIds = eligibleClients.map((client) => client.clientId).filter((id): id is string => !isNil(id));
      if (clientIds.length === 0) return;

      const { data } = await bulkUpdateTaxEnrollmentsToNotInterested({
        variables: {
          input: {
            ids: clientIds,
          },
        },
      });
      const failedIds = data?.bulkUpdateTaxEnrollmentsToNotInterested?.failedIds ?? [];

      if (failedIds.length > 0) {
        console.error('Failed to mark some households as not interested:', failedIds);
        toast.error(`Failed to mark ${failedIds.length} household(s) as not interested. Please try again.`);
      } else {
        toast.success(`Successfully marked ${eligibleClients.length} household(s) as not interested.`);
      }
      await invalidateAndRefetchCaches();
    } catch (error) {
      handleMutationError(error, 'Failed to mark households as not interested. Please try again.');
      console.error('Failed to bulk mark not interested:', error);
    }
  }

  return { bulkMarkNotInterested };
}
